import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './CategoryPage.css';
import AddToCartButton from "../components/AddToCartButton/AddToCartButton";
import BuyNowButton from "../components/BuyNowButton/BuyNowButton";
import { PUBLIC_API_URL } from '../services/api.config';

export default function CategoryPage({ userData, openCartModal }) {
    const { categoryId } = useParams();   // /products/:categoryId
    const { i18n } = useTranslation();
    const navigate = useNavigate();

    const [categoryData, setCategoryData] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');

    // Завантажуємо info про категорію (необов'язково)
    useEffect(() => {
        let isCancelled = false;
        const abortController = new AbortController();

        fetch(`${PUBLIC_API_URL}/categories/list`, {
            signal: abortController.signal
        })
            .then(res => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.text();
            })
            .then(text => {
                if (isCancelled) return;
                
                try {
                    const categories = JSON.parse(text);
                    const foundCat = categories.find(cat => cat.categoryId === categoryId);
                    setCategoryData(foundCat || null);
                } catch (jsonError) {
                    console.error('JSON parsing error in categories:', jsonError, 'Response text:', text);
                }
            })
            .catch(err => {
                if (isCancelled || err.name === 'AbortError') return;
                console.error('Error loading categories:', err);
            });

        return () => {
            isCancelled = true;
            abortController.abort();
        };
    }, [categoryId]);

    // Завантажуємо список товарів для categoryId
    useEffect(() => {
        let isCancelled = false; // Запобігання race conditions

        setLoading(true);
        setErrorMsg('');

        const abortController = new AbortController(); // Для скасування запиту при unmount

        fetch(
            `${PUBLIC_API_URL}/products/category?category=${categoryId}`,
            { signal: abortController.signal }
        )
            .then(res => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.text(); // Спочатку отримуємо як text для кращої обробки
            })
            .then(text => {
                if (isCancelled) return; // Не оновлюємо state якщо компонент unmounted
                
                try {
                    const data = JSON.parse(text);
                    setProducts(Array.isArray(data) ? data : []);
                } catch (jsonError) {
                    console.error('JSON parsing error:', jsonError, 'Response text:', text);
                    throw new Error('Invalid response format');
                }
            })
            .catch(err => {
                if (isCancelled || err.name === 'AbortError') return;
                
                console.error('Error loading products:', err);
                setErrorMsg(err.message || 'Failed to load products');
            })
            .finally(() => {
                if (!isCancelled) {
                    setLoading(false);
                }
            });

        return () => {
            isCancelled = true;
            abortController.abort();
        };
    }, [categoryId]);

    const lang = i18n.language === 'ua' ? 'ua' : 'en';

    // Заголовок категорії
    const pageTitle = categoryData
        ? categoryData.title?.[lang]
        : (lang === 'ua' ? 'Завантаження...' : 'Loading...');
    const pageSubtitle = categoryData
        ? categoryData.tagline?.[lang]
        : '';

    // Швидкий перегляд товару
    const handleQuickView = (e, productId) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/products/${productId}`);
    };

    // Якщо користувач клацає на товар => переходимо на сторінку товару
    const handleProductClick = (productId) => {
        navigate(`/products/${productId}`);
    };

    // Визначаємо, чи це категорія laptop-cases
    const isLaptopCasesCategory = categoryId === 'laptop-cases';
    
    // Встановлюємо стилі залежно від категорії

    if (loading) {
        return (
            <div style={{ padding: '2rem' }}>
                {lang === 'ua' ? 'Завантаження товарів...' : 'Loading products...'}
            </div>
        );
    }
    if (errorMsg) {
        return (
            <div style={{ padding: '2rem', color: 'red' }}>
                {errorMsg}
            </div>
        );
    }

    return (
        <div className="category-page">
                <section
                    className="cases-hero"
                    style={{
                        backgroundImage: categoryData && categoryData.categoryImage ? `url(${categoryData.categoryImage})` : 'none',
                        backgroundColor: (!categoryData || !categoryData.categoryImage) ? '#6c757d' : 'transparent'
                    }}
                >
                        <div className="laptop-cases-overlay"></div>
                        <h1>{pageTitle}</h1>
                        <p className="cases-subtitle">{pageSubtitle}</p>
                </section>

                    <div className="cases-grid">
                        {products.map((item) => {
                            const imageSrc = item.mainImage;

                            // Знаходимо найдешевшу ціну зі списку prices
                            let lowestPrice = null;
                            let selectedParams = {};

                            if (item.prices && item.prices.length > 0) {
                                // Сортуємо ціни за зростанням для поточної мови
                                const sortedPrices = [...item.prices].sort((a, b) => {
                                    const priceA = parseFloat(a.price?.[lang] || 0);
                                    const priceB = parseFloat(b.price?.[lang] || 0);
                                    return priceA - priceB;
                                });

                                // Беремо найдешевшу ціну
                                const cheapestPrice = sortedPrices[0];
                                lowestPrice = cheapestPrice.price?.[lang];

                                // Формуємо параметри для кнопок (без поля price)
                                const { price, ...params } = cheapestPrice;
                                selectedParams = params;
                            }

                            // Якщо ціни немає, показуємо 'N/A'
                            const priceLabel = lowestPrice !== null ? lowestPrice : 'N/A';

                            const titleLabel = item.title?.[lang] || 'Untitled';

                            // Імітація знижки для деяких товарів (рандомно для прикладу)
                            const hasDiscount = item.ProductId % 3 === 0;
                            const oldPrice = hasDiscount ? Math.round(parseFloat(priceLabel) * 1.2) : null;
                            const discountPercent = hasDiscount ? '-20%' : null;

                            // Імітація бейджа для деяких товарів
                            const hasBadge = item.ProductId % 4 === 0;
                            const badgeLabel = hasBadge ? (lang === 'ua' ? 'Новинка' : 'New') : null;

                            return (
                                <div key={item.ProductId} className="case-card">
                                    {hasBadge && <div className="case-badge">{badgeLabel}</div>}

                                    <Link to={`/products/${item.ProductId}`} className="case-image">
                                        <img src={imageSrc} alt={titleLabel}/>
                                        <div className="quick-view" onClick={(e) => handleQuickView(e, item.ProductId)}>
                                            {lang === 'ua' ? 'Швидкий перегляд' : 'Quick View'}
                                        </div>
                                    </Link>

                                    <div className="case-info">
                                        <h2 className="case-title">{titleLabel}</h2>

                                        {/* Ціна з урахуванням знижки */}
                                        <p className="case-price">
                                            {hasDiscount && (
                                                <>
                                            <span className="case-price-old">
                                                {lang === 'ua' ? `${oldPrice} грн` : `$${oldPrice}`}
                                            </span>
                                                    <span className="case-price-discount">{discountPercent}</span>
                                                </>
                                            )}
                                            <span>
                                        {lang === 'ua' ? `${priceLabel} грн` : `$${priceLabel}`}
                                    </span>
                                        </p>

                                        <div className="case-actions">
                                            <AddToCartButton
                                                productId={item.ProductId}
                                                priceParams={selectedParams}
                                                userData={userData}
                                                openCartModal={openCartModal}
                                            >
                                                {lang === 'ua' ? 'Додати в кошик' : 'Add to Cart'}
                                            </AddToCartButton>
                                            <BuyNowButton
                                                productId={item.ProductId}
                                                priceParams={selectedParams}
                                                userData={userData}
                                            >
                                                {lang === 'ua' ? 'Купити зараз' : 'Buy Now'}
                                            </BuyNowButton>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
            }
